import React, { useState, useEffect } from 'react';
import { Mail, CheckCircle2, ChevronRight, Settings2, Sparkles, XCircle, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '@/controllers/api';
import { LocationSelector } from './LocationSelector';
import { useLocationSearch } from '@/hooks/useLocationSearch';

export const NewsletterForm: React.FC = () => {
    const { t } = useTranslation();
    const [step, setStep] = useState<1 | 2 | 3>(1);
    const [enabled, setEnabled] = useState(true);
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [setup, setSetup] = useState({
        focal_length: '',
        sensor_w: '',
        sensor_h: '',
        pixel_size: ''
    });
    const [error, setError] = useState<string | null>(null);

    // Initialisation du hook de recherche de lieu
    const locationSearch = useLocationSearch();

    useEffect(() => {
        // Pré-remplissage depuis le planner si disponible
        const rawConfig = localStorage.getItem('ssiLastLocation');
        if (rawConfig) {
            try {
                const loc = JSON.parse(rawConfig);
                if (loc.name) {
                    locationSearch.setName(loc.name);
                    locationSearch.selectSuggestion({
                        lat: loc.lat,
                        lon: loc.lon,
                        displayName: loc.name
                    });
                }
            } catch (e) {
                console.error('Failed to parse previous location:', e);
            }
        }

        // Pré-remplissage du matériel depuis la config planner
        const rawPlanner = localStorage.getItem('ssiPlannerConfig');
        if (rawPlanner) {
            try {
                const config = JSON.parse(rawPlanner);
                const eq = config?.equipment;
                if (eq) {
                    // Résolution des dimensions capteur
                    const SENSOR_DIMS: Record<string, { w: number; h: number }> = {
                        'aps-c': { w: 22.3, h: 14.9 },
                        'full-frame': { w: 36, h: 24 },
                        'micro-43': { w: 17.3, h: 13 },
                        'one-inch': { w: 13.2, h: 8.8 },
                    };
                    let sensorW = eq.customSensorWidth || '';
                    let sensorH = eq.customSensorHeight || '';
                    if ((!sensorW || !sensorH) && eq.sensorFormat && SENSOR_DIMS[eq.sensorFormat]) {
                        sensorW = sensorW || SENSOR_DIMS[eq.sensorFormat].w;
                        sensorH = sensorH || SENSOR_DIMS[eq.sensorFormat].h;
                    }

                    setSetup({
                        focal_length: eq.focalLength ? String(eq.focalLength) : '',
                        pixel_size: eq.pixelSize ? String(eq.pixelSize) : '',
                        sensor_w: sensorW ? String(sensorW) : '',
                        sensor_h: sensorH ? String(sensorH) : '',
                    });
                }
            } catch { /* ignore */ }
        }

        const checkStatus = async () => {
            try {
                const { newsletterEnabled } = await api.newsletter.getSettings();
                setEnabled(newsletterEnabled);
            } catch (err) {
                console.error('Failed to check newsletter status:', err);
            }
        };
        checkStatus();
    }, []);

    const handleSubscribe = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email || !email.includes('@')) {
            setError(t('newsletter.error'));
            return;
        }
        setError(null);
        setStep(2);
    };

    const handleFinalize = async (skipSetup = false) => {
        setLoading(true);
        setError(null);
        try {
            const payload = {
                email,
                source: 'footer',
                latitude: locationSearch.lat || null,
                longitude: locationSearch.lon || null,
                ...(skipSetup ? {} : {
                    focal_length: setup.focal_length ? parseFloat(setup.focal_length) : null,
                    sensor_w: setup.sensor_w ? parseFloat(setup.sensor_w) : null,
                    sensor_h: setup.sensor_h ? parseFloat(setup.sensor_h) : null,
                    pixel_size: setup.pixel_size ? parseFloat(setup.pixel_size) : null,
                })
            };

            await api.newsletter.subscribe(payload);
            setStep(3);
        } catch (err: any) {
            setError(err.message || t('common.error'));
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (!enabled) {
        return (
            <div className="p-4 bg-white/5 border border-white/10 rounded-xl text-center">
                <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-bold mb-1">{t('newsletter.title')}</p>
                <p className="text-zinc-500 text-[11px]">{t('newsletter.unavailable')}</p>
            </div>
        );
    }

    if (step === 3) {
        return (
            <div className="p-6 bg-primary/10 border border-primary/30 rounded-2xl text-center animate-in fade-in zoom-in duration-500">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-3 border border-primary/40">
                    <Sparkles className="w-6 h-6 text-primary animate-pulse" />
                </div>
                <h3 className="text-sm font-bold text-white mb-1">{t('newsletter.success.title')}</h3>
                <p className="text-[#8dc3ce] text-[11px] leading-relaxed">{t('newsletter.success.message')}</p>
            </div>
        );
    }

    return (
        <div className="relative group">
            {/* Step 1: Email */}
            {step === 1 && (
                <form onSubmit={handleSubscribe} className="space-y-3">
                    <div className="flex flex-col gap-1">
                        <label className="text-[10px] font-bold text-primary uppercase tracking-widest ml-1">{t('newsletter.title')}</label>
                        <p className="text-zinc-400 text-[11px] mb-2 leading-tight">{t('newsletter.subtitle')}</p>
                        <div className="relative">
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={t('newsletter.placeholder')}
                                className="w-full bg-black/40 border border-white/10 focus:border-primary/50 focus:ring-1 focus:ring-primary/20 outline-none rounded-xl px-4 py-3 text-xs text-white placeholder:text-zinc-600 transition"
                                required
                            />
                            <button
                                type="submit"
                                aria-label={t('newsletter.submit')}
                                className="absolute right-1 top-1 bottom-1 px-3 bg-primary hover:bg-cyan-400 text-[#0f2023] font-bold rounded-lg transition-all flex items-center gap-1"
                            >
                                <span className="hidden sm:inline text-[10px]">{loading ? t('newsletter.loading') : t('newsletter.submit')}</span>
                                <ChevronRight className="w-3 h-3" />
                            </button>
                        </div>
                        {error && <p className="text-red-400 text-[9px] mt-1 flex items-center gap-1"><XCircle className="w-2.5 h-2.5" /> {error}</p>}
                    </div>
                </form>
            )}

            {/* Step 2: Customization (Location + Hardware) */}
            {step === 2 && (
                <div className="space-y-4 animate-in slide-in-from-right-4 fade-in duration-300">
                    <div className="space-y-3">
                        <div className="flex items-center gap-2 mb-1">
                            <MapPin className="w-3.5 h-3.5 text-primary" />
                            <h3 className="text-white font-bold text-xs uppercase tracking-tight">{t('newsletter.step2.location.title')}</h3>
                        </div>
                        <LocationSelector
                            value={locationSearch.name}
                            onChange={locationSearch.setName}
                            onUseGPS={locationSearch.useGPS}
                            onSelectSuggestion={locationSearch.selectSuggestion}
                            gpsLoading={locationSearch.gpsLoading}
                            suggestions={locationSearch.suggestions}
                            error={locationSearch.error}
                            gpsError={locationSearch.gpsError}
                        />
                    </div>

                    <div className="space-y-3 border-t border-white/5 pt-4">
                        <div className="flex items-center gap-2 mb-1">
                            <Settings2 className="w-3.5 h-3.5 text-primary" />
                            <h3 className="text-white font-bold text-xs uppercase tracking-tight">{t('newsletter.step2.hardware.title')}</h3>
                        </div>
                        <p className="text-zinc-400 text-[10px] leading-tight mb-2">{t('newsletter.step2.hardware.subtitle')}</p>

                        <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                                <label className="text-[9px] text-zinc-500 uppercase font-medium">{t('newsletter.step2.hardware.focal')}</label>
                                <input
                                    type="number"
                                    placeholder={t('newsletter.step2.hardware.placeholderFocal')}
                                    value={setup.focal_length}
                                    onChange={(e) => setSetup({ ...setup, focal_length: e.target.value })}
                                    className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-[10px] text-white focus:border-primary/50 outline-none placeholder:text-zinc-700"
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[9px] text-zinc-500 uppercase font-medium">{t('newsletter.step2.hardware.pixel')}</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    placeholder={t('newsletter.step2.hardware.placeholderPixel')}
                                    value={setup.pixel_size}
                                    onChange={(e) => setSetup({ ...setup, pixel_size: e.target.value })}
                                    className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-[10px] text-white focus:border-primary/50 outline-none placeholder:text-zinc-700"
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[9px] text-zinc-500 uppercase font-medium">{t('newsletter.step2.hardware.width')}</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    placeholder={t('newsletter.step2.hardware.placeholderWidth')}
                                    value={setup.sensor_w}
                                    onChange={(e) => setSetup({ ...setup, sensor_w: e.target.value })}
                                    className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-[10px] text-white focus:border-primary/50 outline-none placeholder:text-zinc-700"
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[9px] text-zinc-500 uppercase font-medium">{t('newsletter.step2.hardware.height')}</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    placeholder={t('newsletter.step2.hardware.placeholderHeight')}
                                    value={setup.sensor_h}
                                    onChange={(e) => setSetup({ ...setup, sensor_h: e.target.value })}
                                    className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-[10px] text-white focus:border-primary/50 outline-none placeholder:text-zinc-700"
                                />
                            </div>
                        </div>

                        <div className="flex gap-2 pt-1">
                            <button
                                onClick={() => handleFinalize()}
                                disabled={loading}
                                className="flex-grow py-2.5 bg-primary hover:bg-cyan-400 text-[#0f2023] font-bold rounded-xl text-[10px] transition disabled:opacity-50 uppercase tracking-tighter"
                            >
                                {loading ? t('newsletter.step2.actions.processing') : t('newsletter.step2.actions.validate')}
                            </button>
                            <button
                                onClick={() => handleFinalize(true)}
                                className="px-3 py-2.5 bg-white/5 hover:bg-white/10 text-white font-medium rounded-xl text-[10px] transition border border-white/10"
                            >
                                {t('newsletter.step2.actions.skip')}
                            </button>
                        </div>
                        {error && <p className="text-red-400 text-[9px] flex items-center gap-1"><XCircle className="w-2.5 h-2.5" /> {error}</p>}
                    </div>
                </div>
            )}

            {/* Background Glow */}
            <div className="absolute -inset-1 bg-gradient-to-r from-primary/10 to-blue-500/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-10 transition duration-1000 -z-10" />
        </div>
    );
};

